import {
  type DBSchema,
  type IDBPDatabase,
  idb,
} from '@bangle.io/browser-utils';
import { clearLocalWorkspaceYdocs } from '@bangle.io/supabase';

const DB_NAME = 'sariz-notes-supabase-cache';
const DB_VERSION = 1;
const STORE = 'workspaceFiles';

interface SupabaseCacheSchema extends DBSchema {
  workspaceFiles: {
    key: string;
    value: {
      key: string;
      workspaceId: string;
      path: string;
      content: string | null;
      storagePath: string | null;
      contentType: string;
      byteSize: number;
      mtime: string;
    };
    indexes: { workspaceId: string };
  };
}

let dbPromise: Promise<IDBPDatabase<SupabaseCacheSchema>> | null = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = idb.openDB<SupabaseCacheSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'key' });
          store.createIndex('workspaceId', 'workspaceId');
        }
      },
    });
  }
  return dbPromise;
}

function makeKey(workspaceId: string, path: string) {
  return `${workspaceId}:${path}`;
}

export async function cacheWorkspaceFile(input: {
  workspaceId: string;
  path: string;
  content: string | null;
  storagePath: string | null;
  contentType: string;
  byteSize: number;
  mtime: string;
}) {
  const db = await getDb();
  await db.put(STORE, {
    key: makeKey(input.workspaceId, input.path),
    workspaceId: input.workspaceId,
    path: input.path,
    content: input.content,
    storagePath: input.storagePath,
    contentType: input.contentType,
    byteSize: input.byteSize,
    mtime: input.mtime,
  });
}

export async function deleteCachedWorkspaceFile(
  workspaceId: string,
  path: string,
) {
  const db = await getDb();
  await db.delete(STORE, makeKey(workspaceId, path));
}

export async function renameCachedWorkspaceFile(
  workspaceId: string,
  oldPath: string,
  newPath: string,
) {
  const existing = await getCachedWorkspaceFile(workspaceId, oldPath);
  if (!existing) return;
  await deleteCachedWorkspaceFile(workspaceId, oldPath);
  await cacheWorkspaceFile({
    workspaceId,
    path: newPath,
    content: existing.content,
    storagePath: existing.storagePath,
    contentType: existing.contentType,
    byteSize: existing.byteSize,
    mtime: existing.mtime,
  });
}

export async function getCachedWorkspaceFile(
  workspaceId: string,
  path: string,
) {
  const db = await getDb();
  return (await db.get(STORE, makeKey(workspaceId, path))) ?? null;
}

export async function listCachedWorkspaceFiles(workspaceId: string) {
  const db = await getDb();
  const tx = db.transaction(STORE, 'readonly');
  const index = tx.store.index('workspaceId');
  const rows = await index.getAll(workspaceId);
  await tx.done;
  return rows;
}

export async function clearCachedWorkspaceFiles(workspaceId: string) {
  const db = await getDb();
  const tx = db.transaction(STORE, 'readwrite');
  const index = tx.store.index('workspaceId');
  let cursor = await index.openCursor(IDBKeyRange.only(workspaceId));
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
  await tx.done;
}

/**
 * Drop every local mirror of one cloud workspace's notes on this device
 * (the IndexedDB file cache and the local ydoc store). Called after the
 * workspace has been deleted remotely so no note content outlives the
 * deletion locally; callers treat failures as best-effort.
 */
export async function purgeLocalSupabaseWorkspaceCaches(
  workspaceId: string,
): Promise<void> {
  await clearCachedWorkspaceFiles(workspaceId);
  await clearLocalWorkspaceYdocs(workspaceId);
}
